import { toRoman, validRomans } from 'shared/definitions/musicTheory/romanNumerals';
import utils from '../utils';
import KeySignature from '../uTheory.Notation/keySignature';
import RomanNumeral from '../uTheory.Notation/romanNumeral';
import Chord from '../uTheory.Notation/chord';
import rejectDuplicates from './rejectDuplicates';

export interface RomanNumeralQuestionOptions {
  keyQuality: 'Major' | 'minor' | 'both';
  inversions: boolean;
  chordTypes: 'triads' | 'sevenths' | 'both';
}

/**
 * get roman numeral questions in the format "key:roman"
 *
 * @param {integer} numberOfQuestions (>1)
 * @param {object} options chord settings
 * @param {string} options.keyQuality 'Major', 'minor', 'both'; default 'both'
 * @param {boolean} options.inversions default true
 * @param {string} options.chordTypes 'triads', 'sevenths', 'both'; default 'both'
 * @returns {{rn: RomanNumeral, skills: string[]}[]}
 */
export function generateRomanQuestions(
  numberOfQuestions: number,
  options: Partial<RomanNumeralQuestionOptions> = {}
) {
  options = {
    keyQuality: 'both',
    inversions: true,
    chordTypes: 'both',
    ...options,
  };

  // console.log('generateRomanQuestions', options);

  if (!['triads', 'sevenths', 'both'].includes(options.chordTypes)) {
    throw new Error(
      `options.chordTypes must be 'triads', 'sevenths', or 'both', not ${options.chordTypes}`
    );
  }
  if (!['Major', 'minor', 'both'].includes(options.keyQuality)) {
    throw new Error(
      `options.keyQuality must be 'Major', 'Minor', or 'both', not ${options.keyQuality}`
    );
  }
  if (!utils.isInteger(numberOfQuestions)) throw new Error('numberOfQuestions must be an integer.');

  // sd: [ [triadQual, seventhQual], [triadInversions], [seventhInversions] ],
  let keyIsMajor;

  function flatten(array, element) {
    return array.concat(element);
  }

  const rnMapper = (arr, sd) => {
    const qualities = arr[0];
    return qualities
      .map((quality, isSeventh) => {
        if (!quality) return false;
        const inversions = arr[isSeventh + 1];
        return inversions.map((inversion) => ({
          sd,
          inversion,
          quality,
          keyIsMajor,
          isSeventh,
        }));
      })
      .filter((a) => a)
      .reduce(flatten, []);
  };

  let questions: {
    sd: string;
    inversion: number;
    keyIsMajor: boolean;
    quality: string;
    isSeventh: boolean;
  }[] = [];
  if (options.keyQuality === 'minor' || options.keyQuality === 'both') {
    keyIsMajor = false;
    questions.push(...utils.mapToArray(validRomans.minor, rnMapper).reduce(flatten));
  }
  if (options.keyQuality === 'Major' || options.keyQuality === 'both') {
    keyIsMajor = true;
    questions.push(...utils.mapToArray(validRomans.Major, rnMapper).reduce(flatten));
  }

  const numKeys =
    options.keyQuality === 'both' ? Math.ceil(numberOfQuestions / 2) : numberOfQuestions;
  const majorKeys = KeySignature.randomKeys(numKeys, 'Major');
  const minorKeys = KeySignature.randomKeys(numKeys, 'minor');

  // Now flatten and filter out ones that don't fit the limitations:
  questions = questions.filter((question) => {
    // Include sevenths or triads?
    const seventhOk =
      options.chordTypes === 'both' ||
      (question.isSeventh ? options.chordTypes === 'sevenths' : options.chordTypes === 'triads');
    const inversionOk = options.inversions || question.inversion === 0;
    const qualityOk =
      options.keyQuality === 'both' ||
      (question.keyIsMajor ? options.keyQuality === 'Major' : options.keyQuality === 'minor');
    return seventhOk && inversionOk && qualityOk;
  });

  // console.log('questions raw');
  // console.log(questions);

  questions = rejectDuplicates(questions, numberOfQuestions, { adjacentEquality: 'sd' });

  const finalQuestions = utils
    .avoidAdjacentUniques(questions, 'sd')
    .slice(0, numberOfQuestions)
    .map((q) => {
      const key = q.keyIsMajor ? majorKeys.shift() : minorKeys.shift();
      (q.keyIsMajor ? majorKeys : minorKeys).push(key);
      const sd = /[1-7]{1}/.exec(q.sd)[0];
      let rn = toRoman(sd);
      if (q.quality[0] === 'M' || q.quality[0] === 'D') {
        rn = rn.toUpperCase();
      }
      const quality = q.quality[0] === 'o' ? q.quality.replace('7', '') : '';
      const figures = Chord.inversionToFigures(q.inversion, q.isSeventh);
      return {
        rn: new RomanNumeral(`${key}: ${rn}${quality}${figures}`),
        skills: getRNSkills(q),
      };
    });

  return finalQuestions;
}

interface GetRNSkillsOptions {
  sd: string;
  inversion: number;
  isSeventh: boolean;
  keyIsMajor: boolean;
  noRaiseTheLeadingTone?: boolean;
}

/**
 * return skills for a RN question
 *
 * @param {object} obj
 * @param {string} obj.sd
 * @param {number} obj.inversion
 * @param {boolean} obj.isSeventh
 * @param {boolean} obj.keyIsMajor
 * @param {boolean} [obj.noRaiseTheLeadingTone]
 */
export function getRNSkills({
  sd,
  inversion,
  isSeventh,
  keyIsMajor,
  noRaiseTheLeadingTone,
}: GetRNSkillsOptions) {
  const qualitySkill = [
    ['triad_rn_major', 'triad_rn_minor'],
    ['seventh_rn_major', 'seventh_rn_minor'],
  ][Number(isSeventh) * 1][Number(!keyIsMajor) * 1];
  // console.log(qualitySkill);

  const skills = [qualitySkill];
  if (inversion) {
    skills.push('rn_inversion', isSeventh ? 'seventh_chord_figured_bass' : 'triad_figured_bass');
  }
  // eslint-disable-next-line eqeqeq
  if (!noRaiseTheLeadingTone && !keyIsMajor && (sd == '7' || sd == '5' || sd === 'M7')) {
    skills.push('raise_the_leading_tone');
  }
  return skills;
}
